import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { getHeaderTheme } from "../../../config/headerTheme";
import Modal from "../../../components/Modal";
import { useUser } from "../../../hooks/useUser";
import { useHost } from "../../../hooks/userInfoService";
import { useOwnerRestaurantSelection } from "../../../hooks/useOwnerRestaurantSelection";
import { useDeviceType } from "../../../hooks/useMediaQuery";
import QuizList from "../components/QuizList";
import QuizCategoryFilter from "../components/QuizCategoryFilter";
import QuizPlay from "../components/QuizPlay";
import QuizResult from "../components/QuizResult";
import HostLevelCard from "../components/HostLevelCard";
import { useQuizSession, isNoMoreQuestionsError } from "../hooks/useQuizSession";
import { shootQuizConfetti } from "../utils/shootQuizConfetti";
import { buildQuizHeaders } from "../utils/buildQuizHeaders";
import { useQuizTopics } from "../hooks/useQuizTopics";
import { useTenantTopicsAccess } from "../hooks/useTenantTopicsAccess";
import { useQuizTopicProgress } from "../hooks/useQuizTopicProgress";
import { resetQuizTopicProgress } from "../services/quizRuntimeService";

const clampPercentage = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  return Math.max(0, Math.min(100, numeric));
};

const QuizHome = ({ forcePlayable = false }) => {
  const { isHost, isOwner, userId } = useUser();
  const history = useHistory();
  const { infoHost } = useHost();
  const { selectedOwnerRestaurant } = useOwnerRestaurantSelection();
  const canPlayQuizzes = Boolean(isHost || isOwner || forcePlayable);
  const isOwnerPlayer = Boolean(isOwner && !isHost);
  const shouldFilterByTenantTopics = true;

  const headers = useMemo(() => {
    const storedName = typeof window !== "undefined" ? localStorage.getItem("hname") : "";
    const ownerName = selectedOwnerRestaurant?.name || selectedOwnerRestaurant?.restoName || "";
    return buildQuizHeaders({
      userId: userId || undefined,
      userName: isOwnerPlayer ? ownerName || storedName || undefined : infoHost?.name || storedName || undefined,
    });
  }, [infoHost?.name, isOwnerPlayer, selectedOwnerRestaurant?.name, selectedOwnerRestaurant?.restoName, userId]);

  const quizState = useQuizSession({ headers, userId });
  const {
    topicIds: allowedTopicIds,
    loading: loadingAccess,
    error: accessError,
  } = useTenantTopicsAccess({ enabled: true });
  const { topics, loading, error, reload: reloadTopics } = useQuizTopics({
    headers,
    allowedTopicIds: shouldFilterByTenantTopics ? allowedTopicIds : undefined,
    enabled: Boolean(
      canPlayQuizzes &&
      userId &&
      !loadingAccess &&
      (!shouldFilterByTenantTopics || Array.isArray(allowedTopicIds))
    ),
  });
  const {
    topicsProgress,
    progressById: topicProgressById,
    loading: loadingHostProgress,
    error: hostProgressError,
    reload: reloadTopicProgress,
  } = useQuizTopicProgress({
    topics,
    userId,
    headers,
    enabled: Boolean(canPlayQuizzes && userId && !loading && topics.length > 0),
  });
  const [resettingQuizId, setResettingQuizId] = React.useState(null);
  const [selectedCategory, setSelectedCategory] = React.useState(null);

  const quizzes = useMemo(
    () =>
      topics.map((topic) => ({
        id: topic._id,
        title: topic.name,
        description: topic.description,
        topicId: topic._id,
        questionsCount: topic.total_questions,
        avgQualityScore: topic.avg_quality_score,
        totalDocuments: topic.total_documents,
        category: topic.category?.name,
        progressPercentage: clampPercentage(topicProgressById[topic._id]?.progress_percentage || 0),
        topicAverageScore: Number(topicProgressById[topic._id]?.average_score || 0),
        topicTotalQuestions: Number(topicProgressById[topic._id]?.total_questions || 0),
      })),
    [topicProgressById, topics],
  );
  const hostLevelSummary = useMemo(() => {
    const normalizedTopics =
      quizzes.length > 0
        ? quizzes.map((quiz) => ({
            topic_id: quiz.topicId,
            topic_name: quiz.title,
            progress_percentage: clampPercentage(quiz.progressPercentage || 0),
            average_score: Number(quiz.topicAverageScore || 0),
            accuracy_percentage: Number(topicProgressById[quiz.topicId]?.accuracy_percentage || 0),
            total_questions_answered: Number(topicProgressById[quiz.topicId]?.total_questions_answered || 0),
          }))
        : topicsProgress;

    if (normalizedTopics.length === 0 && topicsProgress.length === 0) {
      return null;
    }

    const attemptedTopics = normalizedTopics.filter(
      (topic) =>
        Number(topic?.total_questions_answered || 0) > 0 ||
        clampPercentage(topic?.progress_percentage || 0) > 0
    ).length;
    const overallProgress =
      normalizedTopics.length > 0
        ? normalizedTopics.reduce(
            (acc, topic) => acc + clampPercentage(topic?.progress_percentage || 0),
            0,
          ) /
          normalizedTopics.length
        : 0;
    const averageAccuracy =
      normalizedTopics.length > 0
        ? normalizedTopics.reduce((acc, topic) => acc + Number(topic?.accuracy_percentage || 0), 0) /
          normalizedTopics.length
        : null;

    return {
      overallProgress,
      attemptedTopics,
      totalTopics: normalizedTopics.length,
      averageAccuracy,
      topics: normalizedTopics,
    };
  }, [quizzes, topicProgressById, topicsProgress]);

  const isLoadingQuizzes = loading || loadingAccess;
  const quizzesError = accessError || error;

  const categories = useMemo(
    () => Array.from(new Set(topics.map((topic) => topic.category?.name).filter(Boolean))),
    [topics]
  );

  const filteredQuizzes = useMemo(
    () => (selectedCategory ? quizzes.filter((quiz) => quiz.category === selectedCategory) : quizzes),
    [quizzes, selectedCategory]
  );

  const handleBackToMenu = useCallback(async () => {
    quizState.resetQuiz();
    if (!canPlayQuizzes) return;
    await reloadTopics();
    await reloadTopicProgress();
  }, [canPlayQuizzes, quizState, reloadTopicProgress, reloadTopics]);

  const handleResetTopic = async (quiz) => {
    if (!quiz?.topicId || !userId) return;

    try {
      setResettingQuizId(quiz.id);
      await resetQuizTopicProgress({
        topicId: quiz.topicId,
        userId,
        headers,
      });
      await reloadTopicProgress();
    } catch (err) {
      console.error("Error reseteando progreso del tópico:", err);
    } finally {
      setResettingQuizId(null);
    }
  };

  const { isDesktop } = useDeviceType();

  const handleOpenMarketplace = useCallback(() => {
    const tab = isDesktop ? "trivias-marketplace" : "resto-marketplace";
    history.push({
      pathname: "/",
      search: `tab=${tab}&quizView=marketplace`,
    });
  }, [history, isDesktop]);

  const quizStateRef = useRef(quizState);
  useEffect(() => {
    quizStateRef.current = quizState;
  }, [quizState]);

  const pendingReloadRef = useRef(false);
  const completionConfettiFiredRef = useRef(false);

  useEffect(() => {
    if (quizState.error && isNoMoreQuestionsError(quizState.error)) {
      if (!completionConfettiFiredRef.current) {
        completionConfettiFiredRef.current = true;
        shootQuizConfetti();
      }
    } else {
      completionConfettiFiredRef.current = false;
    }
  }, [quizState.error]);

  useEffect(() => {
    console.log("[QuizHome] auto-start effect running. canPlay:", canPlayQuizzes, "loading:", isLoadingQuizzes, "error:", quizzesError, "quizzesCount:", quizzes.length);
    if (!canPlayQuizzes) { console.log("[QuizHome] blocked: canPlayQuizzes false"); return; }
    if (quizStateRef.current.selectedQuiz || quizStateRef.current.isStarting) { console.log("[QuizHome] blocked: already playing"); return; }

    const pendingQuizId = localStorage.getItem("pendingQuizId");
    console.log("[QuizHome] pendingQuizId:", pendingQuizId);
    if (!pendingQuizId) return;

    if (isLoadingQuizzes) { console.log("[QuizHome] blocked: still loading"); return; }
    if (quizzesError) { console.log("[QuizHome] blocked: quizzes error"); return; }

    const quiz = quizzes.find((q) => String(q.topicId) === String(pendingQuizId));
    console.log("[QuizHome] found quiz:", quiz);
    if (quiz) {
      console.log("[QuizHome] starting quiz:", quiz.title);
      localStorage.removeItem("pendingQuizId");
      pendingReloadRef.current = false;
      quizStateRef.current.handleStart(quiz);
    } else if (!pendingReloadRef.current) {
      console.log("[QuizHome] quiz not found, reloading topics");
      pendingReloadRef.current = true;
      reloadTopics();
    } else {
      console.log("[QuizHome] quiz not found after reload, giving up");
      localStorage.removeItem("pendingQuizId");
    }
  }, [canPlayQuizzes, isLoadingQuizzes, quizzesError, quizzes, reloadTopics]);

  return (
    <Page>
      <Content>
        {!quizState.selectedQuiz && (
          <>
            {isDesktop && <DesktopViewTitle>Trivias</DesktopViewTitle>}
            <ViewSubtitle>Juega, desafía tu conocimiento y gana puntos.</ViewSubtitle>
            {isOwnerPlayer && (
              <CanjearPuntosButton type="button" onClick={handleOpenMarketplace}>
                Canjear puntos
              </CanjearPuntosButton>
            )}
            {canPlayQuizzes && (
              <HostLevelCard
                summary={hostLevelSummary}
                loading={loadingHostProgress}
                error={hostProgressError}
              />
            )}
            {isLoadingQuizzes && <StatusText>Cargando trivias...</StatusText>}
            {quizzesError && <StatusText $error>{quizzesError}</StatusText>}
            {!isLoadingQuizzes && !quizzesError && quizzes.length === 0 && isOwnerPlayer && (
              <EmptyState>
                <StatusText>Aún no tienes trivias. Ve al Marketplace para agregar temas.</StatusText>
                <MarketplaceButton type="button" onClick={handleOpenMarketplace}>
                  Ir al Marketplace
                </MarketplaceButton>
              </EmptyState>
            )}
            {!isLoadingQuizzes && !quizzesError && quizzes.length === 0 && !isOwnerPlayer && (
              <StatusText>No hay trivias disponibles.</StatusText>
            )}
            {!isLoadingQuizzes && !quizzesError && filteredQuizzes.length > 0 && (
              <>
                {categories.length > 0 && (
                  <QuizCategoryFilter
                    categories={categories}
                    selectedCategory={selectedCategory}
                    onSelect={setSelectedCategory}
                  />
                )}
                <QuizList
                  quizzes={filteredQuizzes}
                  onSelect={quizState.handleStart}
                  onReset={handleResetTopic}
                  isHost={canPlayQuizzes}
                  isStarting={quizState.isStarting}
                  resettingQuizId={resettingQuizId}
                />
              </>
            )}
            {!isLoadingQuizzes && !quizzesError && quizzes.length > 0 && filteredQuizzes.length === 0 && (
              <StatusText>No hay trivias en esta categoría.</StatusText>
            )}
          </>
        )}

        {quizState.selectedQuiz && quizState.showResult && (
          <QuizResult
            quiz={quizState.selectedQuiz}
            correctCount={quizState.correctCount}
            onRestart={handleBackToMenu}
          />
        )}

        {quizState.selectedQuiz && !quizState.showResult && (
          <QuizPlay
            question={quizState.currentQuestion}
            questionNumber={quizState.questionNumber}
            totalQuestions={quizState.totalQuestions}
            selectedAnswer={quizState.selectedAnswer}
            correctAnswerIndex={quizState.correctAnswerIndex}
            hasAnswered={quizState.hasAnswered}
            lastResultIsCorrect={quizState.lastResultIsCorrect}
            progress={quizState.progress}
            timeLeft={quizState.timeLeft}
            loading={quizState.loading}
            error={quizState.error}
            onAnswer={quizState.answerQuestion}
            onSubmit={quizState.submitAnswer}
            onNext={quizState.loadNextQuestion}
            onBackToMenu={handleBackToMenu}
            showFeedback={quizState.showFeedback}
            feedback={quizState.feedback}
            answerExplanation={quizState.answerExplanation}
            isFetchingNext={quizState.isFetchingNext}
            timedOut={quizState.timedOut}
            streak={quizState.streak}
            activeMilestone={quizState.activeMilestone}
            clearActiveMilestone={quizState.clearActiveMilestone}
          />
        )}

        {isNoMoreQuestionsError(quizState.error) ? (
          <Modal
            isOpen={Boolean(quizState.error)}
            onClose={handleBackToMenu}
            onAccept={handleBackToMenu}
            label="¡Trivia completada!"
            acceptButtonText="Volver a mis trivias"
            cancelButton={false}
          >
            <CompletionMessage>
              <span role="img" aria-label="celebration">🎉</span>
              <strong>¡Felicitaciones!</strong>
              <p>Has completado esta trivia al 100%.</p>
            </CompletionMessage>
          </Modal>
        ) : (
          <Modal
            isOpen={Boolean(quizState.error)}
            onClose={quizState.clearError}
            onAccept={quizState.clearError}
            label="Trivias"
            acceptButtonText="OK"
            cancelButton={false}
          >
            <ModalMessage>{quizState.error}</ModalMessage>
          </Modal>
        )}
      </Content>
    </Page>
  );
};

export default QuizHome;

const Page = styled.section`
  min-height: calc(100vh - 140px);
  padding: 12px 0 90px;

  @media (max-width: 768px) {
    padding: 12px 0 80px;
  }
`;

const Content = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  box-sizing: border-box;
`;

const StatusText = styled.p`
  margin: 12px 0;
  font-size: 16px;
  color: ${(props) => (props.$error ? "#dc2626" : "#6b7280")};
`;

const EmptyState = styled.div`
  display: grid;
  gap: 12px;
  justify-items: start;
  margin: 12px 0;
`;

const theme = getHeaderTheme();

const MarketplaceButton = styled.button`
  border: 0;
  border-radius: 8px;
  background: ${theme.primary};
  color: ${theme.text};
  cursor: pointer;
  font-size: 16px;
  font-weight: 700;
  padding: 10px 16px;
  transition: filter 0.2s ease;

  &:hover {
    filter: brightness(0.92);
  }
`;

const ViewSubtitle = styled.p`
  margin: 0 0 12px;
  color: #6b7280;
  font-size: 15px;
  font-weight: 500;
  text-align: center;

  @media (min-width: 768px) {
    text-align: left;
  }
`;

const CanjearPuntosButton = styled(MarketplaceButton)`
  display: block;
  width: 100%;
  margin-bottom: 16px;

  @media (min-width: 768px) {
    width: auto;
  }
`;

const DesktopViewTitle = styled.h2`
  margin: 0 0 12px;
  font-size: 24px;
  font-weight: 700;
  color: #111827;
`;

const ModalMessage = styled.p`
  margin: 0;
  text-align: center;
  color: #374151;
  font-size: 16px;
  line-height: 1.5;
  padding-bottom:1rem;
`;

const CompletionMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
  padding-bottom: 1rem;

  span {
    font-size: 50px;
    line-height: 1;
  }

  strong {
    font-size: 20px;
    color: #111827;
  }

  p {
    margin: 0;
    font-size: 16px;
    color: #374151;
    line-height: 1.5;
  }
`;
